import {useCallback, useEffect, useRef, useState} from 'react';
import {DragStart, DropResult} from 'react-beautiful-dnd';

import {useAppSelector} from 'store/config/hooks';
import {selectUserInfo} from 'store/slices/auth-slice';

import {
    TReturningDraggableComponent,
    TDraggableComponentProps,
} from './types';
import {
    isOverSwapArea,
    moveToNewColumn,
    normalMove,
    swapBetweenColumns,
    swapInSameColumn,
} from './utils';

const enrichDraggableComponentsWithId = (
    draggableComponents: TDraggableComponentProps[][],
) => draggableComponents.map((draggableBlock, offset) => (
        draggableBlock.map((draggableProps, idx) => ({
            id: `item-${offset}-${idx}-${new Date().getTime()}`,
            ...draggableProps,
        }))));

export const useDraggableComponents = (
    draggableArray: TDraggableComponentProps[][],
    persistKey?: string,
    swapAreaClassName?: string,
) => {
    const user = useAppSelector(selectUserInfo);
    const persistUserAwareKey = persistKey && user ? `${persistKey}-${user?.userName}-${user?.userId}` : null;
    const persistedComponents = persistUserAwareKey ? localStorage.getItem(persistUserAwareKey) : null;

    const [components, setComponents] = useState<TReturningDraggableComponent[][]>(
        persistedComponents
            ? JSON.parse(persistedComponents)
            : enrichDraggableComponentsWithId(draggableArray) as unknown as TReturningDraggableComponent[][],
    );

    const [isDragging, setIsDragging] = useState(false);
    const [draggingId, setDraggingId] = useState<string | null>(null);

    // Реф для точных координат, сохранённых в момент mouseup (захват события)
    const lastMouseUpCoordsRef = useRef({x: 0, y: 0});

    const handleMouseUp = useCallback((e: MouseEvent) => {
        lastMouseUpCoordsRef.current = {x: e.clientX, y: e.clientY};
    }, []);

    // persist current draggable state
    useEffect(() => {
        if (persistUserAwareKey) {
            localStorage.setItem(persistUserAwareKey, JSON.stringify(components));
        }
    }, [components, persistUserAwareKey]);

    const finishDrag = () => {
        setIsDragging(false);
        setDraggingId(null);
    }

    const handleDragStart = useCallback((start: DragStart) => {
        // для MacOS
        document?.querySelector('body')?.classList.add('dragging');
        setIsDragging(true);
        setDraggingId(start.draggableId);
        window.addEventListener('mouseup', handleMouseUp, true);
    }, [handleMouseUp]);

    // Завершение драга
    const handleDragEnd = useCallback((result: DropResult) => {
        // Убираем перехватчик mouseup
        window.removeEventListener('mouseup', handleMouseUp, true);
        document?.querySelector('body')?.classList.remove('dragging');

        // Получаем координаты из захваченного mouseup
        const {x, y} = lastMouseUpCoordsRef.current;
        // Сбрасываем для следующего драга
        lastMouseUpCoordsRef.current = {x: 0, y: 0};

        const {destination, source} = result;

        if (!destination) {
            if (components.length >= components.flat(2).length) {
                setIsDragging(false);
                setDraggingId(null);
                return;
            }
            const sourceIndex = +source.droppableId;
            const destinationIndex = components.length + 1;
            const newPosition = moveToNewColumn(
                components[sourceIndex],
                source,
                destinationIndex,
            );
            const newState = [...components];
            newState[sourceIndex] = newPosition[sourceIndex];
            newState[destinationIndex] = newPosition[destinationIndex];
            setComponents(newState.filter(col => col.length));
            setIsDragging(false);
            setDraggingId(null);
            return;
        }

        const sourceIndex = +source.droppableId;
        const destinationIndex = +destination.droppableId;

        // Проверяем swap-зоны
        const swapAreas = swapAreaClassName ? document.querySelectorAll(swapAreaClassName) : null;
        const shouldSwap = isOverSwapArea(x, y, swapAreas);

        if (shouldSwap) {
            if (sourceIndex === destinationIndex) {
                swapInSameColumn(sourceIndex, source.index, destination.index, setComponents);
                finishDrag();
                return;
            }
            if (destination.index < components[destinationIndex].length) {
                swapBetweenColumns(sourceIndex, destinationIndex, source.index, destination.index, setComponents);
                finishDrag();
                return;
            }
        }

        normalMove(sourceIndex, destinationIndex, source, destination, setComponents);
        finishDrag();
    }, [components, handleMouseUp]);

    return {
        components,
        isDragging,
        draggingId,
        handleDragStart,
        handleDragEnd,
    };
};
